import assert from 'assert';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';

/** Generic helper function to get the published flowchart version from a procedure. This supports both the
 * legacy and Procedures on Workflows (POW) config versions, and favors the config version when it is
 * available.
 *
 * @param procedure - The procedure to get the published flowchart version from.
 * @returns The published flowchart version.
 */
export function getPowAgnosticPublishedFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion;
    };
    publishedProcedureFlowchartConfigVersion?: TPowVersion;
  },
>(
  procedure: T
): T['publishedProcedureFlowchartConfigVersion'] | T['procedureFlowchart']['publishedFlowchartVersion'] {
  return (
    procedure.publishedProcedureFlowchartConfigVersion ??
    procedure.procedureFlowchart.publishedFlowchartVersion
  );
}

/**
 * Extracts the published procedure flowchart version ID from a procedure, or
 * the latest flowchart version if publishing is disabled.  Returns null if no
 * published flowchart version is available or if it is inaccessible.
 *
 * @param procedure - The procedure to extract the published flowchart version ID from.
 * @returns The ID of the published or latest flowchart version depending on
 * publishing status, or null if not available.
 */
export function getPowAgnosticPublishedFlowchartVersionId<
  TLegacyVersion extends {id: string; __typename: 'ProcedureFlowchartVersionNode'},
  TPowVersion extends {id: string; __typename: 'ProcedureFlowchartConfigVersionNode'},
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      publishedFlowchartVersion?: TLegacyVersion | null;
    };
    publishedProcedureFlowchartConfigVersion?: TPowVersion | null;
  },
>(procedure: T): string | null {
  const publishedFlowchartVersion = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure);
  if (publishedFlowchartVersion == null) {
    return null;
  }
  return getAccessibleOrInaccessibleId(publishedFlowchartVersion);
}

/** Generic helper function to get the latest flowchart version from a procedure. This supports both the
 * legacy and Procedures on Workflows (POW) config versions, and favors the config version when it is
 * available.
 *
 * @param procedure - The procedure to get the latest flowchart version from.
 * @returns The latest flowchart version.
 */
export function getPowAgnosticLatestFlowchartVersionFromProcedure<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureNode';
    procedureFlowchart: {
      __typename: 'ProcedureFlowchartNode';
      latestFlowchartVersion: TLegacyVersion;
    };
    latestProcedureFlowchartConfigVersion?: TPowVersion;
  },
>(
  procedure: T
): T['latestProcedureFlowchartConfigVersion'] | T['procedureFlowchart']['latestFlowchartVersion'] {
  return (
    procedure.latestProcedureFlowchartConfigVersion ?? procedure.procedureFlowchart.latestFlowchartVersion
  );
}

/** Generic helper function to get the template flowchart version from a procedure run. This supports both the
 * legacy flowchart version (ProcedureFlowchartVersionNode) and the Procedures on Workflows (POW) config version
 * (ProcedureFlowchartConfigVersionNode), and favors the config version when it is available.
 *
 * In this function, there are 3 potential return types: ProcedureFlowchartConfigVersionNode,
 * ProcedureFlowchartVersionNode, and InaccessibleInfoNode. While it is possible for procedureFlowchartConfigVersion
 * to be null, it is not possible for templateProcedureFlowchartVersion to be null. Thus, this function will
 * never return null. In terms of the NonNullable type generic, we are defining the return type to be
 * NonNullable<T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']>, because
 * typescript is having a hard time narrowing the return type correctly when TPowVersion and TLegacyVersion are both
 * made of union of types.
 *
 * Why is doing this safe? This is safe, because we can guarantee that we will never have null as
 * templateProcedureFlowchartVersion is not nullable. Also, we are doing a casting to non-nullable while
 * still maintaining the rest of the type received for T['procedureFlowchartConfigVersion'] and
 * T['templateProcedureFlowchartVersion'].
 *
 * @param procedureRun - The procedure run to get the template flowchart version from.
 * @returns The template flowchart version (config version if available, else legacy). Never returns null.
 */
export function getPowAgnosticTemplateFlowchartVersionFromProcedureRun<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunNode';
    templateProcedureFlowchartVersion: TLegacyVersion;
    procedureFlowchartConfigVersion?: TPowVersion | null;
  },
>(
  procedureRun: T
): NonNullable<T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']> {
  return (procedureRun.procedureFlowchartConfigVersion ??
    procedureRun.templateProcedureFlowchartVersion) as NonNullable<
    T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']
  >;
}

/** Generic helper function to get the template flowchart version from a procedure run plan. This supports both the
 * legacy flowchart version (ProcedureFlowchartVersionNode) and the Procedures on Workflows (POW) config version
 * (ProcedureFlowchartConfigVersionNode), and favors the config version when it is available.
 *
 * In this function, there are 3 potential return types: ProcedureFlowchartConfigVersionNode,
 * ProcedureFlowchartVersionNode, and InaccessibleInfoNode. While it is possible for procedureFlowchartConfigVersion
 * to be null, it is not possible for templateProcedureFlowchartVersion to be null. Thus, this function will
 * never return null. In terms of the NonNullable type generic, we are defining the return type to be
 * NonNullable<T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']>, because
 * typescript is having a hard time narrowing the return type correctly when TPowVersion and TLegacyVersion are both
 * made of union of types.
 *
 * Why is doing this safe? This is safe, because we can guarantee that we will never have null as
 * templateProcedureFlowchartVersion is not nullable. Also, we are doing a casting to non-nullable while
 * still maintaining the rest of the type received for T['procedureFlowchartConfigVersion'] and
 * T['templateProcedureFlowchartVersion'].
 *
 * @param procedureRunPlan - The procedure run plan to get the template flowchart version from.
 * @returns The template flowchart version (config version if available, else legacy). Never returns null.
 */
export function getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunPlanNode';
    templateProcedureFlowchartVersion: TLegacyVersion;
    procedureFlowchartConfigVersion?: TPowVersion | null;
  },
>(
  procedureRunPlan: T
): NonNullable<T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']> {
  return (procedureRunPlan.procedureFlowchartConfigVersion ??
    procedureRunPlan.templateProcedureFlowchartVersion) as NonNullable<
    T['procedureFlowchartConfigVersion'] | T['templateProcedureFlowchartVersion']
  >;
}

/** Extracts the procedure from a flowchart config version or a procedure flowchart version node.
 *
 * Important: When using this function with a ProcedureFlowchartVersionNode, ensure you are passing a flowchart
 * version that is parented by a procedure, not a procedure run. If you are working with a procedure run,
 * to obtain the procedure, you should use the templateProcedureFlowchartVersion instead of the
 * currentProcedureFlowchartVersion.
 *
 * Why are we asserting that the procedure is non-null? Why is this safe? This is because each procedure flowchart,
 * should always have a parent defined. However, the procedure flowchart graphql resolver returns null when
 * the flowchart parent is a ProcedureRun, which is not ideal. Alternatively, this could be fixed by updating
 * the graphql resolver to return the parent as a ProcedureRun instead of null, but procedures team determined
 * that this is not worth the effort to update as soon it will be deprecated. Asserting non-null is safe,
 * because you are expected to pass only flowchart versions that is expected to be parented by a procedure, which
 * in that case the parent will always be non-null (e.g. templateProcedureFlowchartVersion).
 *
 * This function expects the parent property to be of type ProcedureNode already so typescript linting should
 * catch any cases where you trying to pass in the incorrect flowchart version. Additionally, this function
 * throws an assertion error if the __typename of the parent is not ProcedureNode.
 *
 * @param flowchartVersion - Either a `ProcedureFlowchartConfigVersionNode` (PoW) or a
 *   `ProcedureFlowchartVersionNode` (legacy). Should be a template version, not a plan version.
 * @returns The parent `ProcedureNode` that owns this flowchart version.
 */
export function getPowAgnosticProcedureFromFlowchartVersion<TProcedure extends {__typename: 'ProcedureNode'}>(
  flowchartVersion:
    | {
        __typename: 'ProcedureFlowchartConfigVersionNode';
        procedure: TProcedure;
      }
    | {
        __typename: 'ProcedureFlowchartVersionNode';
        procedureFlowchart: {__typename: 'ProcedureFlowchartNode'; parent: TProcedure | null};
      }
): TProcedure {
  if (flowchartVersion.__typename === 'ProcedureFlowchartConfigVersionNode') {
    return flowchartVersion.procedure;
  }
  const parent = flowchartVersion.procedureFlowchart.parent;
  assert(parent != null, 'Expected the procedure flowchart parent to be set');
  assert(
    parent.__typename === 'ProcedureNode',
    'Expected the procedure flowchart parent to be of type ProcedureNode'
  );
  return parent;
}

/** Generic helper function to get the executing flowchart version from a procedure run.
 *
 * This supports both the flowchart config version and the current flowchart version, and favors the
 * flowchart config version when it is available. Falls back to current flowchart version if the flowchart config
 * version is null.
 *
 * The return type here is T['procedureFlowchartConfigVersion'] | T['currentProcedureFlowchartVersion']
 * because we want users to have control over how to handle null, inaccessible or accessible flowchart versions.
 *
 * TLegacyVersion and TPowVersion do not extend their respective types because Typescript does not do a
 * good job at inferring the correct types when we are returning union of types.
 *
 * @param procedureRun - The procedure run to get the executing flowchart version from.
 * @returns The executing flowchart version (flowchart config version if available, else current flowchart version), or null if both are null.
 */
export function getPowAgnosticExecutingFlowchartVersionFromProcedureRun<
  TLegacyVersion,
  TPowVersion,
  T extends {
    __typename: 'ProcedureRunNode';
    currentProcedureFlowchartVersion?: TLegacyVersion;
    procedureFlowchartConfigVersion?: TPowVersion;
  },
>(procedureRun: T): T['procedureFlowchartConfigVersion'] | T['currentProcedureFlowchartVersion'] {
  return procedureRun.procedureFlowchartConfigVersion ?? procedureRun.currentProcedureFlowchartVersion;
}

/** Extracts the procedure ID from a procedure run by resolving the template flowchart version
 * (PoW config version or legacy) and navigating to the owning procedure. Returns null if the flowchart
 * version is inaccessible.
 *
 * @param procedureRun - The procedure run to extract the procedure ID from.
 * @returns The ID of the owning procedure, or null if the flowchart version is inaccessible.
 */
export function getPowAgnosticProcedureIdFromProcedureRun<
  TProcedure extends {__typename: 'ProcedureNode'; id: string},
  TLegacyVersion extends
    | {
        __typename: 'ProcedureFlowchartVersionNode';
        procedureFlowchart: {__typename: 'ProcedureFlowchartNode'; parent: TProcedure | null};
      }
    | {__typename: 'InaccessibleInfoNode'},
  TPowVersion extends
    | {__typename: 'ProcedureFlowchartConfigVersionNode'; procedure: TProcedure}
    | {__typename: 'InaccessibleInfoNode'},
  T extends {
    __typename: 'ProcedureRunNode';
    templateProcedureFlowchartVersion: TLegacyVersion;
    procedureFlowchartConfigVersion?: TPowVersion | null;
  },
>(procedureRun: T): string | null {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  if (isInaccessible(flowchartVersion)) {
    return null;
  }
  return getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion).id;
}

/** Returns the flowchart version ID to use when navigating to a procedure version.
 *
 * For legacy flowchart versions that have been migrated to PoW, this returns the migrated
 * PoW version's ID so the user is directed to the converted procedure rather than the
 * legacy version that no longer renders.
 */
export function getFlowchartVersionIdForNavigation(
  flowchartVersion:
    | {__typename: 'ProcedureFlowchartConfigVersionNode'; id: string}
    | {
        __typename: 'ProcedureFlowchartVersionNode';
        id: string;
        migratedToFlowchartConfigVersion?:
          | {__typename: 'ProcedureFlowchartConfigVersionNode'; id: string}
          | {__typename: 'InaccessibleInfoNode'}
          | null;
      }
): string {
  if (
    flowchartVersion.__typename === 'ProcedureFlowchartVersionNode' &&
    flowchartVersion.migratedToFlowchartConfigVersion != null &&
    !isInaccessible(flowchartVersion.migratedToFlowchartConfigVersion)
  ) {
    return flowchartVersion.migratedToFlowchartConfigVersion.id;
  }
  return flowchartVersion.id;
}
